import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, Alert, CircularProgress, Chip, Table, TableHead, TableBody,
  TableRow, TableCell, FormControlLabel, Switch,
} from '@mui/material';
import {
  Refresh as RefreshIcon, CheckCircleOutline as ResolveIcon, HealthAndSafety as HealthIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL;

const SOURCE_COLOR = { SYNC: 'default', MONITOR: 'info', FRONTEND_PREVIEW: 'secondary' };

const firstError = (d) => {
  const errs = d.validationErrors?.errors;
  if (Array.isArray(errs) && errs.length) {
    return `${errs[0].message || errs[0].error || 'error'}${errs.length > 1 ? ` (+${errs.length - 1} more)` : ''}`;
  }
  return '-';
};

const when = (iso) => {
  try { return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

/**
 * Admin view of the validation-discrepancy corpus (ADR-0006): flows Meta flagged invalid. The
 * "only our misses" filter surfaces the cases our own builder did NOT flag, the ones worth
 * learning from. Resolving marks a discrepancy triaged.
 */
const ValidationHealthPanel = () => {
  const { authenticatedFetch } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [missesOnly, setMissesOnly] = useState(false);
  const [resolving, setResolving] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authenticatedFetch(
        `${API_URL}/api/admin/validation-discrepancies?missesOnly=${missesOnly}&limit=100`
      );
      if (!res.ok) throw new Error(`Failed to load (${res.status})`);
      setRows(await res.json());
    } catch (err) {
      setError(err.message || 'Failed to load validation discrepancies');
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch, missesOnly]);

  useEffect(() => { load(); }, [load]);

  const resolve = async (id) => {
    setResolving(id);
    try {
      const res = await authenticatedFetch(
        `${API_URL}/api/admin/validation-discrepancies/${id}/resolve`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: 'Triaged from admin' }) }
      );
      if (!res.ok) throw new Error(`Failed to resolve (${res.status})`);
      setRows((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      setError(err.message || 'Failed to resolve');
    } finally {
      setResolving(null);
    }
  };

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <HealthIcon color="primary" />
        <Typography variant="subtitle1" fontWeight="bold" sx={{ flexGrow: 1 }}>
          Validation health
        </Typography>
        <FormControlLabel
          control={<Switch size="small" checked={missesOnly} onChange={(e) => setMissesOnly(e.target.checked)} />}
          label="Only our misses"
        />
        <Button size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Flows Meta flagged invalid. &ldquo;Our misses&rdquo; are the cases our own builder did not catch,
        the ones to learn from so we flag them before Meta does.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : rows.length === 0 ? (
        <Alert severity="success">
          No unresolved {missesOnly ? 'misses' : 'discrepancies'}, our validation is in parity with Meta.
        </Alert>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Source</TableCell>
              <TableCell>Builder</TableCell>
              <TableCell>Meta flow</TableCell>
              <TableCell>Error</TableCell>
              <TableCell>When</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((d) => (
              <TableRow key={d.id} hover>
                <TableCell><Chip size="small" label={d.source} color={SOURCE_COLOR[d.source] || 'default'} variant="outlined" /></TableCell>
                <TableCell>
                  {d.builderFlaggedInvalid === false
                    ? <Chip size="small" color="error" label="Missed it" />
                    : d.builderFlaggedInvalid === true
                      ? <Chip size="small" color="success" variant="outlined" label="Flagged" />
                      : <Chip size="small" variant="outlined" label="Unknown" />}
                </TableCell>
                <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{d.whatsappFlowId || '-'}</Typography></TableCell>
                <TableCell sx={{ maxWidth: 320 }}><Typography variant="caption" noWrap title={firstError(d)}>{firstError(d)}</Typography></TableCell>
                <TableCell><Typography variant="caption">{when(d.capturedAt)}</Typography></TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    startIcon={resolving === d.id ? <CircularProgress size={14} /> : <ResolveIcon />}
                    disabled={resolving != null}
                    onClick={() => resolve(d.id)}
                  >
                    Resolve
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </Box>
      )}
    </Paper>
  );
};

export default ValidationHealthPanel;
